/**
 * The locale and region registry.
 *
 * ## Region and language are separate axes
 *
 * They are routinely conflated, and conflating them is what forces a rewrite
 * the first time a market needs two languages. A pharmacy marketplace hits
 * that quickly: Wales is the same regulatory market as England but not the
 * same language, and Ireland shares a language with the UK but not a medicines
 * regulator.
 *
 * So a region carries *which* languages it offers, and a locale identifies the
 * dictionary. Adding Welsh to the UK is a new entry in `languages`; adding
 * Ireland is a new region reusing the `en` dictionary. Neither touches a
 * component.
 *
 * ## What is actually translated today
 *
 * English only. `en-GB` is the complete dictionary; `en` is a thin override
 * layer over it for wording that is wrong outside the UK — the GPhC is the
 * British regulator, so the Global footer must not claim registration with it.
 *
 * That is a real localisation difference rather than a placeholder, which is
 * the point: the provider is exercised by content that genuinely varies, not
 * by a duplicate dictionary pretending to be a second language.
 *
 * No other language is listed, because none is written. A flag in a menu is a
 * promise, and offering a language that falls back silently to English is a
 * promise broken at the moment someone accepts it.
 */

/** A BCP 47 tag. Narrow on purpose: an unknown tag should not typecheck. */
export type Locale = 'en-GB' | 'en';

/** Resolved first, and the guaranteed floor of every lookup. */
export const DEFAULT_LOCALE: Locale = 'en-GB';

/**
 * Where a locale looks when it lacks a key.
 *
 * `en-GB` terminates the chain — it is the only dictionary required to be
 * complete, which is enforced by the type of the dictionary module.
 */
export const FALLBACK_OF: Readonly<Record<Locale, Locale | undefined>> = {
  'en-GB': undefined,
  en: 'en-GB',
};

export interface LanguageOption {
  readonly locale: Locale;
  /** In the language itself. A language named in English is named for us. */
  readonly endonym: string;
  /** For people who do not read the endonym's script. */
  readonly englishName: string;
}

export interface Region {
  readonly code: string;
  readonly name: string;
  /** Emoji rather than an image: no asset request, and it inherits the font. */
  readonly flag: string;
  /** Free-text terms the region search should also match. */
  readonly aliases: readonly string[];
  readonly languages: readonly LanguageOption[];
}

/**
 * Named rather than reached for as `REGIONS[0]`.
 *
 * Every lookup below needs a guaranteed region to fall back to, and with
 * `noUncheckedIndexedAccess` an index into an array cannot supply one. A named
 * constant makes the guarantee real instead of asserted away with `!`.
 */
const UNITED_KINGDOM: Region = {
  code: 'GB',
  name: 'United Kingdom',
  flag: '🇬🇧',
  aliases: ['uk', 'britain', 'great britain', 'england', 'scotland', 'wales', 'northern ireland'],
  languages: [{ locale: 'en-GB', endonym: 'English', englishName: 'English' }],
};

const GLOBAL: Region = {
  code: 'GLOBAL',
  name: 'Global',
  flag: '🌍',
  aliases: ['international', 'worldwide', 'other', 'rest of world'],
  languages: [{ locale: 'en', endonym: 'English', englishName: 'English' }],
};

export const REGIONS: readonly Region[] = [UNITED_KINGDOM, GLOBAL];

/** The language every fallback path ends at. */
const DEFAULT_LANGUAGE: LanguageOption = {
  locale: DEFAULT_LOCALE,
  endonym: 'English',
  englishName: 'English',
};

/** The region a locale belongs to, for rendering the header's current choice. */
export function regionForLocale(locale: Locale): Region {
  return (
    REGIONS.find((region) => region.languages.some((language) => language.locale === locale)) ??
    UNITED_KINGDOM
  );
}

export function languageForLocale(locale: Locale): LanguageOption {
  const region = regionForLocale(locale);
  return region.languages.find((language) => language.locale === locale) ?? DEFAULT_LANGUAGE;
}

/**
 * Filters regions by a free-text query.
 *
 * Matches the region name, its aliases and its language names, so "welsh",
 * "britain" and "english" all find the United Kingdom. A region that matches
 * keeps all its languages: narrowing the language list under a matched region
 * hides options rather than helping.
 */
export function searchRegions(query: string): readonly Region[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return REGIONS;

  return REGIONS.filter((region) => {
    const haystack = [
      region.name,
      region.code,
      ...region.aliases,
      ...region.languages.flatMap((language) => [language.endonym, language.englishName]),
    ];
    return haystack.some((term) => term.toLowerCase().includes(needle));
  });
}

/** Narrows an unknown string — a persisted value, say — to a supported locale. */
export function asLocale(value: unknown): Locale | undefined {
  return REGIONS.flatMap((region) => region.languages).find((language) => language.locale === value)?.locale;
}
